import React, { useMemo } from 'react';
import styled from 'styled-components';

import { Inner, Text } from '~/styles';

import { TimelineEvent } from './TimelineEvent';
import { TimelineEntry } from './types';

interface Props {
  entries: TimelineEntry[];
  returnEntries?: TimelineEntry[];
}

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(200)};
`;

const StyledText = styled(Text)`
  margin-bottom: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    margin-bottom: 24px;
  }
`;

export const Timeline: React.FC<Props> = ({ entries, returnEntries = [] }) => {
  const allEntries = useMemo(() => {
    const combined = [...entries];
    if (returnEntries.length > 0) {
      const existingReturnLabels = new Set(
        entries.map((entry) => {
          if (entry.internationalWireReturnData) {
            return entry.label;
          }
          if (entry.returnData) {
            return entry.label;
          }
          return null;
        })
      );
      const uniqueReturnEntries = returnEntries.filter((entry) => {
        if (entry.internationalWireReturnData) {
          return !existingReturnLabels.has(entry.label);
        }
        return !existingReturnLabels.has(entry.label);
      });
      combined.push(...uniqueReturnEntries);
    }

    return combined.sort((a, b) => {
      const dateA = a.date instanceof Date ? a.date : new Date(a.date || '');
      const dateB = b.date instanceof Date ? b.date : new Date(b.date || '');

      const timeA = isNaN(dateA.getTime()) ? Infinity : dateA.getTime();
      const timeB = isNaN(dateB.getTime()) ? Infinity : dateB.getTime();
      return timeA - timeB;
    });
  }, [entries, returnEntries]);

  const entriesWithDates = useMemo(() => {
    return allEntries.filter((entry) => entry.date);
  }, [allEntries]);

  return (
    <TimelineWrapper>
      <Inner px="var(--page-margin)">
        <StyledText as="h3" size={16} color="foreground" weight="medium">
          Timeline
        </StyledText>
        {allEntries.map((entry, idx) => {
          const isFirstWithDate = Boolean(entry.date && entry === entriesWithDates[0]);
          const isLastWithDate = Boolean(entry.date && entry === entriesWithDates[entriesWithDates.length - 1]);

          return (
            <TimelineEvent
              key={`event-${idx}`}
              amount={entry.amount}
              batch={entry.batch}
              date={entry.date}
              label={entry.label}
              link={entry.link}
              status={entry.status}
              partyData={entry.partyData}
              returnData={entry.returnData}
              internationalWireReturnData={entry.internationalWireReturnData}
              reviewData={entry.reviewData}
              showDate={isFirstWithDate || isLastWithDate}
            />
          );
        })}
      </Inner>
    </TimelineWrapper>
  );
};
